import { useCallback, useState } from 'react';
import { useSharedLinks } from './useSharedLinks';
import { 
  getRecordSharedLinks, 
  deactivateSharedLink, 
  generateShareUrl 
} from '@/integrations/sharing/sharingService';
import type { SharedLink } from '@/integrations/supabase/types';

export interface UseSharedLinksExtendedReturn {
  // Базовая функциональность из useSharedLinks
  isLoading: boolean;
  
  // Расширенная функциональность
  checkExistingSharedLink: (recordId: string) => Promise<SharedLink | null>;
  handleShareAction: (params: {
    recordId: string;
    onManageExisting: (link: SharedLink) => void;
    onCreateNew: () => void;
  }) => Promise<void>;
  deactivateExistingLink: (linkId: string, onDeactivated?: () => void) => Promise<boolean>;
  isRecordShared: (recordId: string) => Promise<boolean>;
  getSharedLinkUrl: (recordId: string) => Promise<string | null>;
}

export const useSharedLinksExtended = (): UseSharedLinksExtendedReturn => {
  const baseSharedLinks = useSharedLinks();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Проверяет наличие активной публичной ссылки для записи
   */
  const checkExistingSharedLink = useCallback(async (recordId: string): Promise<SharedLink | null> => {
    try {
      const result = await getRecordSharedLinks(recordId);
      
      if (Array.isArray(result)) {
        // Находим первую активную ссылку
        const activeLink = result.find(link => link.is_active === true);
        return activeLink || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error checking existing shared link:', error);
      return null;
    }
  }, []);

  /**
   * Обрабатывает действие "Поделиться" - проверяет существующую ссылку или создает новую
   */
  const handleShareAction = useCallback(async (params: {
    recordId: string;
    onManageExisting: (link: SharedLink) => void;
    onCreateNew: () => void;
  }) => {
    const { recordId, onManageExisting, onCreateNew } = params;
    
    try {
      setIsLoading(true);
      const existingLink = await checkExistingSharedLink(recordId);
      
      if (existingLink) {
        // Если есть активная ссылка - открываем диалог управления
        onManageExisting(existingLink);
      } else {
        // Если нет активной ссылки - открываем диалог создания
        onCreateNew();
      }
    } catch (error) {
      console.error('Error handling share action:', error);
      // В случае ошибки по умолчанию показываем диалог создания
      onCreateNew();
    } finally {
      setIsLoading(false);
    }
  }, [checkExistingSharedLink]);

  /**
   * Деактивирует существующую публичную ссылку
   */
  const deactivateExistingLink = useCallback(async (
    linkId: string, 
    onDeactivated?: () => void
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const result = await deactivateSharedLink(linkId);
      
      if (typeof result === 'boolean' && result === true) {
        onDeactivated?.();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error deactivating shared link:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Проверяет, опубликована ли запись (есть ли активная ссылка)
   */
  const isRecordShared = useCallback(async (recordId: string): Promise<boolean> => {
    try {
      const existingLink = await checkExistingSharedLink(recordId);
      return existingLink !== null;
    } catch (error) {
      console.error('Error checking if record is shared:', error);
      return false;
    }
  }, [checkExistingSharedLink]);

  /**
   * Получает URL публичной ссылки для записи (если есть)
   */
  const getSharedLinkUrl = useCallback(async (recordId: string): Promise<string | null> => {
    try {
      const existingLink = await checkExistingSharedLink(recordId);
      
      if (existingLink) {
        return generateShareUrl(existingLink.token);
      }
      
      return null;
    } catch (error) {
      console.error('Error getting shared link URL:', error);
      return null;
    }
  }, [checkExistingSharedLink]);

  return {
    // Состояние загрузки (локальное или базовое)
    isLoading: isLoading || baseSharedLinks.isLoading,
    
    // Расширенные методы
    checkExistingSharedLink,
    handleShareAction,
    deactivateExistingLink,
    isRecordShared,
    getSharedLinkUrl,
  };
};